import React from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { clearCredentials } from "../app/features/authSlice";
import { useLogoutMutation } from "../app/api/authApi";

const ADMIN_LINKS = [
  { label: "Project Briefs", href: "/admin" },
  { label: "Account", href: "/admin/account" },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const [logout] = useLogoutMutation();

  async function handleLogout() {
    try {
      await logout().unwrap();
    } catch {
      // Logout is stateless server-side — clearing locally is enough
    }
    dispatch(clearCredentials());
    navigate("/admin/login", { replace: true });
  }

  const initials = user
    ? `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase()
    : "";

  return (
    <div className="min-h-screen bg-itl-cream font-sans text-itl-navy">
      <header className="bg-itl-navy text-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 h-[68px] flex items-center justify-between gap-6">
          <div className="flex items-center gap-8 min-w-0">
            <Link to="/admin" className="flex items-center gap-3 shrink-0">
              <img
                src="/images/logo-white.png"
                alt="Incubator Tech Limited"
                className="h-8 w-auto object-contain"
              />
              <span className="hidden sm:block text-[10px] font-semibold uppercase tracking-[0.2em] text-white/50 border-l border-white/20 pl-3">
                Admin
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              {ADMIN_LINKS.map((link) => {
                const active =
                  link.href === "/admin"
                    ? location.pathname === "/admin"
                    : location.pathname.startsWith(link.href);
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`text-[12px] font-medium uppercase tracking-wide pb-0.5 border-b-2 transition-colors duration-200 ${
                      active
                        ? "border-itl-blue text-white"
                        : "border-transparent text-white/60 hover:text-white"
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            <div className="hidden sm:flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-itl-blue grid place-items-center text-[11px] font-semibold">
                {initials}
              </div>
              <div className="leading-tight">
                <div className="text-[12px] font-medium">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-white/45">
                  {user?.role}
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/70 hover:text-white border border-white/20 hover:border-white/50 rounded-full px-4 py-2 transition-colors duration-200"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-6 lg:px-12 py-10">
        <Outlet />
      </main>
    </div>
  );
}
